<template>
    <tailwind-layout :title="'Maintenance '+subscriptionType+' Subscription'">
        <template #header>
            <div class="mb-4 px-4 sm:px-6 lg:px-8 mx-auto flex justify-between items-center">
                <h1 class="font-semibold text-2xl leading-tight">Maintenance {{ subscriptionType }} Subscription</h1>
            </div>
        </template>
        <div class="overflow-hidden shadow-xl rounded-lg p-6 bg-white">
            <div class="grid gap-4 grid-cols-1">
                
                <div v-if="subscription" class="flex flex-col p-4 justify-between">
                    <template v-if="subscription.is_cancellation_requested">
                        <alert-card :type="'warning'">
                            <p class="font-medium">You have requested to cancel your subscription.</p>
                            <p>Your subscription will end on <strong>{{ subscription.stripe_sub_ends_at_formatted }}</strong>.</p>
                            <p>Until then, you can continue using the Maintenance Portal.</p>
                        </alert-card>

                        <button
                            :disabled="isSubmitting"
                            @click.prevent="handleSubscription('resume')"
                            class="primaryButton disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {{ isSubmitting ? 'Processing...' : 'Resume My Subscription' }}
                        </button>
                    </template>
                    <template v-else>
                        <h2 class="text-2xl font-semibold text-green-800 mb-4">You're Subscribed</h2>
                        <h4 class="text-xl">Next Payment: ${{ subscription.plan_amount }} on {{ subscription.stripe_sub_ends_at_formatted }}</h4>
                        <h5 class="text-sm">Canceling your subscription will prevent you from managing maintenance requests on your properties.</h5>
                        <button
                            :disabled="isSubmitting"
                            @click.prevent="handleSubscription('cancel')"
                            class="dangerButton disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {{ isSubmitting ? 'Processing...' : 'Cancel My Subscription' }}
                        </button>
                    </template>
                </div>

                <!-- 86bcd9hg3 (decision 18): maintenance requests are part of RiskGuard Pro now, not sold
                     on their own. The page redirects anyone without this subscription, so this
                     only shows if it ended while the page was open. -->
                <div v-else class="flex flex-col items-center gap-4 p-6 max-w-2xl mx-auto text-center">
                    <h2 class="text-2xl font-semibold text-gray-900">Maintenance requests are part of RiskGuard Pro</h2>
                    <p class="text-gray-700">Upgrade your account to Pro to get them on every property.</p>
                    <a :href="proUpgradeLink.href" class="primaryButton px-6 py-3 text-base font-semibold">
                        {{ proUpgradeLink.label }}
                    </a>
                </div>
            </div>
        </div>

    </tailwind-layout>
</template>

<script>
import TailwindLayout from '@/Layouts/TailwindLayout.vue';
import { router } from '@inertiajs/vue3';
import AlertCard from '@/Shared/AlertCard.vue';
import { proUpgradeNav } from '@/Layouts/proUpgradeNav';

export default {
    components: {
        TailwindLayout,
        AlertCard
    },
    props: {
        subscription: Object,
        is_allowed_trial: Boolean,
        payment_method: Object,
        monthly_fee: String,
    },
    data() {
      return {
        selectedPlan: 'monthly',
        isSubmitting: false,
      }  
    },
    computed: {
        // Basic → pricing pop-up, earlier plan → Billing "Switch to new plan", failed payment → Billing.
        proUpgradeLink() {
            return proUpgradeNav(this.$page.props.auth?.user, route)
                ?? { label: 'Upgrade to Pro', href: route('pricing', { upgrade: 1 }) };
        },
        subscriptionType() {
            if (!this.subscription) return '';
            if (!this.subscription.stripe_sub_ends_at) return '';
            
            const endDate = new Date(this.subscription.stripe_sub_ends_at);
            const startDate = new Date(this.subscription.created_at);
            
            // Calculate the difference in months
            const monthsDiff = (endDate.getFullYear() - startDate.getFullYear()) * 12 + 
                              (endDate.getMonth() - startDate.getMonth());
            
            // If difference is less than 12 months, it's monthly
            // If difference is 12 months or more, it's yearly
            return monthsDiff < 12 ? 'Monthly' : 'Yearly';
        },
    },
    methods: {
        handleSubscription(type) {
            if (this.isSubmitting) return;

            this.isSubmitting = true;

            const routes = {
                resume: route('maintenance.subscription.resume'),
                cancel: route('maintenance.monthly-subscription.cancel'),
            };

            const url = routes[type];

            if (!url) {
                console.error(`Invalid subscription type: ${type}`);
                this.isSubmitting = false;
                return;
            }

            router.visit(url, {
                method: 'post',
                onFinish: () => {
                this.isSubmitting = false;
                },
            });
        },
    },

}
</script>